import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  IconButton,
  Link as MuiLink,
  makeStyles,
  Paper,
  Popper,
  Typography,
} from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { useLocation } from 'wouter';
import useNotificationStore from '../../store/notifications';

const useStyles = makeStyles((theme) => ({
  bellButton: {
    color: theme.palette.light?.main || '#667085',
  },
  popper: {
    zIndex: theme.zIndex.modal + 1,
    width: 360,
    maxHeight: 480,
  },
  paper: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 480,
    boxShadow: theme.shadows[8],
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
  },
  list: {
    overflowY: 'auto',
    flex: 1,
  },
  item: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  unread: {
    backgroundColor: theme.palette.action.selected,
  },
  itemTitle: {
    fontWeight: 600,
    fontSize: 13,
    lineHeight: 1.3,
  },
  itemMessage: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  itemTime: {
    fontSize: 11,
    color: theme.palette.text.disabled,
    marginTop: 4,
  },
  providerLink: {
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

function formatTimeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const NotificationBell = () => {
  const classes = useStyles();
  const [, setLocation] = useLocation();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    startPolling,
    stopPolling,
  } = useNotificationStore();

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const handleToggle = () => {
    if (!open) {
      fetchNotifications();
    }
    setOpen((prev) => !prev);
  };

  const handleClose = () => setOpen(false);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    const { metadata } = notification;
    if (metadata?.care_request_id) {
      setLocation(`/request/${metadata.care_request_id}`);
    }

    setOpen(false);
  };

  const handleProviderClick = (e, metadata) => {
    e.stopPropagation();
    if (metadata?.vendor_id) {
      setLocation(`/providers/${metadata.vendor_id}`);
    }
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        className={classes.bellButton}
        onClick={handleToggle}
        size="small"
      >
        <Badge
          badgeContent={unreadCount}
          color="secondary"
          overlap="rectangular"
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className={classes.popper}
        disablePortal={false}
        modifiers={{
          offset: { enabled: true, offset: '0, 8' },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper className={classes.paper}>
            <Box className={classes.header}>
              <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => markAllAsRead()}
                >
                  Mark all read
                </Button>
              )}
            </Box>

            <Divider />

            <Box className={classes.list}>
              {notifications.length === 0 ? (
                <Typography className={classes.empty}>
                  No notifications yet
                </Typography>
              ) : (
                notifications.map((notification) => {
                  const { metadata = {} } = notification;

                  return (
                    <Box
                      key={notification.id}
                      className={`${classes.item} ${
                        !notification.is_read ? classes.unread : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <Typography className={classes.itemTitle}>
                        {notification.title}
                      </Typography>

                      <Typography className={classes.itemMessage}>
                        {notification.message}
                      </Typography>

                      {metadata.vendor_id && metadata.provider_name && (
                        <MuiLink
                          component="span"
                          className={classes.providerLink}
                          color="primary"
                          onClick={(e) => handleProviderClick(e, metadata)}
                        >
                          {metadata.provider_name}
                        </MuiLink>
                      )}

                      {notification.created_at && (
                        <Typography className={classes.itemTime}>
                          {formatTimeAgo(notification.created_at)}
                        </Typography>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default NotificationBell;
